import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router';

import { Dialog, Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { PercentCrop, PixelCrop } from 'react-image-crop';
import { invariant } from 'ts-invariant';

import { TrackViewItem, useAnalytics } from '@boc/analyticsV3';
import { useCart } from '@boc/framework/checkout/cart';
import { TwPicture } from '@boc/images';
import { getAttribute } from '@boc/framework/products/attributes';
import type {
  ProductDetailsFragment,
  ProductVariantDetailsFragment,
} from '@boc/framework/saleor-graphql';

import { cn } from '~/styling';
import toast from '~/toast';
import { absoluteify, imgUrl, urls } from '~/urls';

import { buttonVariants } from '~/local/components/button';
import { Price } from '~/local/components/price';
import { determineOptimalAspect, generateCenterCrop } from '~/local/custom-puzzle/auto-crop';
import { CropEditorModal } from '~/local/custom-puzzle/components/crop-editor-modal';
import { canvasPreview } from '~/local/custom-puzzle/image-upload/canvasPreview';
import { calcIsLowRes } from '~/local/custom-puzzle/resolution';
import type { CustomPuzzleImageMetadata } from '~/local/custom-puzzle/types';
import { parseImageMetadata, stringifyImageMetadata } from '~/local/custom-puzzle/types';
import { groupPuzzlesByShape, isCirclePuzzle } from '~/local/custom-puzzle/utils';

const getPieces = (variant: ProductDetailsFragment) => {
  const regexPieces = new RegExp(/(?<pieces>\d+) Piece/);
  const matchPieces = variant.name.match(regexPieces)?.groups?.pieces;

  return matchPieces ? Number(matchPieces) : 0;
};

interface CustomPuzzleFlowSheetProps {
  isOpen: boolean;
  onClose: () => void;
  products: ProductDetailsFragment[];
  params: {
    locale: string;
    channel: string;
  };
  initialSlug?: string;
  originalUploadUrl: string;
  croppedUploadUrl: string;
}

// Step Section Wrapper
const StepSection = ({
  number,
  title,
  completed,
  children,
}: {
  number: number;
  title: string;
  completed: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
            completed ? 'bg-green-100 text-green-700' : 'bg-primary-2-10 text-primary-2-80'
          )}
        >
          {completed ? <CheckCircleIcon className="h-5 w-5" /> : number}
        </div>
        <h3
          data-testid={`step-section-title-${number}`}
          className="font-heading-condensed text-primary-2-80 text-lg font-medium"
        >
          {title}
        </h3>
      </div>
      <div className="ml-11">{children}</div>
    </div>
  );
};

// Step 1: Select Puzzle Type
const SelectPuzzleType = ({
  products,
  selectedSlug,
  onSelect,
  scrollToUpload,
}: {
  products: ProductDetailsFragment[];
  selectedSlug: string | undefined;
  onSelect: (slug: string) => void;
  scrollToUpload?: () => void;
}) => {
  const sortedProducts = products
    .filter((p) => p.isAvailable)
    .sort((a, b) => getPieces(a) - getPieces(b));

  const handleSelect = (slug: string) => {
    onSelect(slug);
    setTimeout(() => {
      scrollToUpload?.();
    }, 100);
  };

  return (
    <div className="space-y-4">
      {groupPuzzlesByShape(sortedProducts).map((group) => (
        <div key={group.shape} data-testid={`puzzle-shape-${group.shape.toLowerCase()}`}>
          <div className="text-neutral-2-60 mb-2 text-xs font-medium tracking-wide uppercase">
            {group.shape}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {group.products.map((product) => {
              const isSelected = selectedSlug === product.slug;

              return (
                <button
                  data-testid={`puzzle-type-${product.slug}`}
                  key={product.slug}
                  onClick={() => handleSelect(product.slug)}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-all',
                    isSelected
                      ? 'border-primary-2 bg-primary-2-10'
                      : 'border-neutral-2-20 hover:border-neutral-2-40 bg-white'
                  )}
                >
                  <div className="text-neutral-2-80 text-sm font-medium">
                    {product.name.replace('Piece', 'pc')}
                  </div>
                  <div className="text-neutral-2-60 mt-1 text-xs">
                    ~{Math.round(Number(getAttribute(product.attributes, 'length')?.values[0].name))} x{' '}
                    {Math.round(Number(getAttribute(product.attributes, 'width')?.values[0].name))} cm
                  </div>
                  {product.variants && (
                    <div className="text-primary-2-80 mt-2 font-medium">
                      <Price
                        label="price"
                        price={product.variants[0].pricing?.price?.gross}
                        truncate={true}
                      />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// Step 2: Upload Image
const UploadImage = ({
  selectedProduct,
  params,
  originalUploadUrl,
  croppedUploadUrl,
  onSuccess,
  onProceedToCheckout,
}: {
  selectedProduct: ProductDetailsFragment;
  params: { locale: string; channel: string };
  originalUploadUrl: string;
  croppedUploadUrl: string;
  onSuccess: (checkoutId: string) => void;
  onProceedToCheckout: (lineId?: string) => void;
}) => {
  invariant(selectedProduct?.variants);
  const productVariant = selectedProduct.variants[0];

  const { addItem, isAddingItem, refetch, updateItem, cart } = useCart();
  const tracker = useAnalytics();

  const width = Number(getAttribute(selectedProduct.attributes, 'width')?.values[0].name);
  const height = Number(getAttribute(selectedProduct.attributes, 'length')?.values[0].name);
  const isCircle = isCirclePuzzle(selectedProduct.attributes);

  const dpi = 75;
  const cm_in_an_inch = 2.54;

  const minWidth = Math.ceil(((width / cm_in_an_inch) * dpi) / 1) * 1;
  const minHeight = Math.ceil(((height / cm_in_an_inch) * dpi) / 1) * 1;

  // Upload state management
  const [uploadState, setUploadState] = useState<{
    isUploading: boolean;
    error: string | null;
    isLowRes: boolean;
  }>({
    isUploading: false,
    error: null,
    isLowRes: false,
  });

  // Uploaded data management
  const [uploadedData, setUploadedData] = useState<{
    imageUrl: string;
    metadata: CustomPuzzleImageMetadata;
    lineId: string;
    croppedUploadUrl: string;
    quantity: number;
  } | null>(null);

  // Crop editor modal state
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [isUpdatingCrop, setIsUpdatingCrop] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  // Ref for the success section to scroll to after upload
  const successSectionRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to success section when upload completes
  useEffect(() => {
    if (uploadedData && successSectionRef.current) {
      setTimeout(() => {
        successSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [uploadedData]);

  const productUrl = useCallback(
    (item: ProductVariantDetailsFragment) =>
      absoluteify(`${urls.customPuzzle.step2({ locale: params.locale, channel: params.channel })}?variant=${item.sku}`),
    [params.locale, params.channel]
  );

  const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return 'Only .jpg, .jpeg, .png and .webp formats are supported.';
    }
    if (file.size > 20000000) {
      return 'File is too large, file size must be less than 20mb';
    }

    return null;
  };

  const handleFileSelect = async (file: File) => {
    const error = validateFile(file);

    if (error) {
      setUploadState({ isUploading: false, error, isLowRes: false });

      return;
    }

    setUploadState({ isUploading: true, error: null, isLowRes: false });

    try {
      const reader = new FileReader();

      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.addEventListener('load', () => resolve(reader.result?.toString() || ''));
        reader.addEventListener('error', reject);
        reader.readAsDataURL(file);
      });

      const img = new Image();

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const { naturalWidth, naturalHeight } = img;

          const newLowRes = calcIsLowRes(naturalHeight, naturalWidth, minHeight, minWidth);

          if (newLowRes) {
            setUploadState({ isUploading: false, error: null, isLowRes: true });
            reject(new Error('Image resolution too low'));

            return;
          }

          resolve();
        };
        img.onerror = reject;
        img.src = dataUrl;
      });

      // Upload original image
      await fetch(originalUploadUrl, {
        method: 'PUT',
        body: file,
      });

      const originalUrl = new URL(originalUploadUrl);
      const originalImageUrl = `${originalUrl.origin}${originalUrl.pathname}`;

      // Generate center-based crop
      const puzzleAspect = height / width;
      const optimalAspect = determineOptimalAspect(
        img.naturalWidth,
        img.naturalHeight,
        puzzleAspect
      );
      const percentCrop = generateCenterCrop(img.naturalWidth, img.naturalHeight, optimalAspect);

      // Convert percent crop to pixel crop
      const pixelCrop: PixelCrop = {
        x: (percentCrop.x * img.naturalWidth) / 100,
        y: (percentCrop.y * img.naturalHeight) / 100,
        width: (percentCrop.width * img.naturalWidth) / 100,
        height: (percentCrop.height * img.naturalHeight) / 100,
        unit: 'px',
      };

      // Create canvas and generate cropped image
      const canvas = document.createElement('canvas');

      await canvasPreview(img, canvas, pixelCrop);

      const croppedBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to create blob'));
            } else {
              resolve(blob);
            }
          },
          'image/png',
          1
        );
      });

      // Upload cropped image
      await fetch(croppedUploadUrl, {
        method: 'PUT',
        body: croppedBlob,
      });

      const croppedUrl = new URL(croppedUploadUrl);
      const croppedImageUrl = `${croppedUrl.origin}${croppedUrl.pathname}`;

      // Create metadata
      const metadata: CustomPuzzleImageMetadata = {
        originalUrl: originalImageUrl,
        croppedUrl: croppedImageUrl,
        crop: {
          x: percentCrop.x,
          y: percentCrop.y,
          width: percentCrop.width,
          height: percentCrop.height,
        },
        originalDimensions: {
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
      };

      // Track add to cart
      tracker.trackAddToCart({
        variant: { ...productVariant, product: selectedProduct },
        quantity: 1,
        itemUrl: productUrl,
        checkout: cart,
      });

      // Add item to cart with metadata (force new line to prevent merging)
      await addItem(
        productVariant.id,
        1,
        [
          {
            key: 'imageUrl',
            value: stringifyImageMetadata(metadata),
          },
        ],
        true // forceNewLine - each custom puzzle should be a separate line
      );

      // Refetch cart to get latest data
      const updatedCart = await refetch();

      if (updatedCart) {
        // Find the line we just added (the one with matching cropped URL in metadata)
        const addedLine = updatedCart.lines.find((line) => {
          const imageMeta = line.metadata.find((m) => m.key === 'imageUrl');

          if (imageMeta) {
            try {
              const parsed = JSON.parse(imageMeta.value);

              return parsed.croppedUrl === croppedImageUrl;
            } catch {
              return false;
            }
          }

          return false;
        });

        // Set uploaded data to show success state
        if (addedLine) {
          setIsImageLoading(true);
          setUploadedData({
            imageUrl: croppedImageUrl,
            metadata,
            lineId: addedLine.id,
            croppedUploadUrl,
            quantity: addedLine.quantity,
          });
        }
        setUploadState({ isUploading: false, error: null, isLowRes: false });

        onSuccess(updatedCart.id);
      } else {
        throw new Error('Failed to load cart');
      }
    } catch (_error) {
      if (!uploadState.isLowRes) {
        toast.error('Failed to process image, please try again.', {
          id: 'custom-puzzle-upload-error',
        });
        setUploadState((prev) => ({ ...prev, isUploading: false }));
      }
    }
  };

  const isLoading = uploadState.isUploading || isAddingItem;
  const hasError = !!uploadState.error || uploadState.isLowRes;

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleEditCrop = () => {
    setIsCropModalOpen(true);
  };

  const handleSaveCrop = async (crop: PercentCrop, croppedBlob: Blob) => {
    if (!uploadedData) return;

    setIsUpdatingCrop(true);
    setIsImageLoading(true);

    try {
      // Extract the S3 key from the cropped URL
      const croppedUrl = new URL(uploadedData.metadata.croppedUrl);
      const s3Key = croppedUrl.pathname.substring(1); // Remove leading slash

      // Get a fresh presigned upload URL
      const uploadUrlResponse = await fetch(
        `/api/custom-puzzle/upload-url?key=${encodeURIComponent(s3Key)}`
      );

      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }
      const { uploadUrl } = await uploadUrlResponse.json();

      // Generate timestamp for cache busting
      const timestamp = Date.now();

      // Upload new cropped image to S3 using presigned URL
      await fetch(uploadUrl, {
        method: 'PUT',
        body: croppedBlob,
      });

      // Update metadata with new crop coordinates and timestamp
      const updatedMetadata: CustomPuzzleImageMetadata = {
        ...uploadedData.metadata,
        crop: {
          x: crop.x,
          y: crop.y,
          width: crop.width,
          height: crop.height,
        },
        timestamp,
      };

      // Update line item metadata in cart
      await updateItem(uploadedData.lineId, uploadedData.quantity, [
        {
          key: 'imageUrl',
          value: stringifyImageMetadata(updatedMetadata),
        },
      ]);

      // Update local state with new image URL (with timestamp for cache busting)
      const newImageUrl = `${croppedUrl.origin}${croppedUrl.pathname}?t=${timestamp}`;

      setUploadedData({
        ...uploadedData,
        imageUrl: newImageUrl,
        metadata: updatedMetadata,
      });

      setIsCropModalOpen(false);
    } catch (error) {
      console.error('Failed to update crop:', error);
      toast.error('Failed to update crop, please try again.', {
        id: 'custom-puzzle-crop-error',
      });
      setIsImageLoading(false);
    } finally {
      setIsUpdatingCrop(false);
    }
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
  };

  return (
    <>
      <TrackViewItem variant={{ ...productVariant, product: selectedProduct }} itemUrl={productUrl} />

      <div className="space-y-3">
        <div className="bg-primary-4-10 text-primary-4 rounded-lg px-3 py-2 text-xs">
          Min resolution: {minHeight}x{minWidth}px
        </div>

        {uploadedData ? (
          <div className="space-y-4">
            <div className="bg-neutral-2-10 relative overflow-hidden rounded-lg border-2 border-green-500">
              <div
                className={cn(isCircle && 'overflow-hidden rounded-full')}
                style={{ aspectRatio: `${width} / ${height}` }}
              >
                <TwPicture
                  imgUrl={imgUrl}
                  src={uploadedData.imageUrl}
                  defaultSize={400}
                  alt="Uploaded custom puzzle preview"
                  className="h-full w-full object-contain"
                  onLoad={handleImageLoad}
                />
              </div>
              {isImageLoading ? (
                <div className="bg-opacity-75 absolute inset-0 flex items-center justify-center bg-white">
                  <div className="border-primary-2 h-12 w-12 animate-spin rounded-full border-4 border-t-transparent"></div>
                </div>
              ) : (
                <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-green-500 px-3 py-1.5 text-xs font-medium text-white shadow">
                  <CheckCircleIcon className="h-4 w-4 shrink-0" />
                  <div className="text-sm font-medium">Image uploaded successfully!</div>
                </div>
              )}
            </div>
            <div ref={successSectionRef} className="flex gap-3">
              <button
                className={cn(
                  buttonVariants({ intent: 'secondary' }),
                  'flex flex-1 items-center justify-center'
                )}
                onClick={handleEditCrop}
                type="button"
                disabled={isImageLoading || isUpdatingCrop}
              >
                <PencilIcon className="mr-2 h-4 w-4" />
                Edit Crop
              </button>
              <button
                className={cn(buttonVariants({ intent: 'primary' }), 'flex-1')}
                onClick={() => onProceedToCheckout()}
                type="button"
                disabled={isImageLoading || isUpdatingCrop}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              disabled={isLoading}
              onChange={handleFileInputChange}
              className="hidden"
              id="puzzle-image-upload"
            />
            <label
              htmlFor="puzzle-image-upload"
              className={cn(
                'flex h-[200px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed transition-colors',
                isLoading
                  ? 'opacity-50'
                  : hasError
                    ? 'cursor-pointer border-red-400 hover:border-red-500'
                    : 'border-neutral-2-30 hover:border-primary-2 cursor-pointer'
              )}
            >
              {hasError ? (
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              ) : (
                <CloudArrowUpIcon className="text-neutral-2-60 h-8 w-8" />
              )}
              <div className="text-neutral-2-80 text-center text-sm">
                {uploadState.error && <div className="mb-1 text-red-500">{uploadState.error}</div>}
                {uploadState.isLowRes && (
                  <div className="mb-1 text-red-500">
                    Min resolution: {minHeight}x{minWidth}px required
                  </div>
                )}
                <div>{isLoading ? 'Processing...' : 'Click to select image'}</div>
              </div>
            </label>
          </div>
        )}
      </div>

      {uploadedData && (
        <CropEditorModal
          isOpen={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageUrl={uploadedData.metadata.originalUrl}
          initialCrop={{
            unit: '%',
            x: uploadedData.metadata.crop.x,
            y: uploadedData.metadata.crop.y,
            width: uploadedData.metadata.crop.width,
            height: uploadedData.metadata.crop.height,
          }}
          puzzleWidth={width}
          puzzleHeight={height}
          circular={isCircle}
          onSave={handleSaveCrop}
        />
      )}
    </>
  );
};

export function CustomPuzzleFlowSheet({
  isOpen,
  onClose,
  products,
  params,
  initialSlug,
  originalUploadUrl,
  croppedUploadUrl,
}: CustomPuzzleFlowSheetProps) {
  const navigate = useNavigate();

  const [selectedSlug, setSelectedSlug] = useState<string | undefined>(initialSlug);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [uploadComplete, setUploadComplete] = useState(false);

  const uploadSectionRef = useRef<HTMLDivElement>(null);

  // Handle browser back button
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = () => {
      onClose();
    };

    // Push a new state when modal opens
    window.history.pushState({ modal: 'sheet' }, '');
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  const scrollToUpload = useCallback(() => {
    uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleClose = () => {
    setSelectedSlug(initialSlug);
    setCheckoutId(null);
    setUploadComplete(false);
    onClose();
  };

  const handleProceedToCheckout = (lineId?: string) => {
    if (checkoutId) {
      onClose(); // Close the modal first
      const cartUrl = lineId
        ? `${urls.checkout.cart(params)}?editLine=${lineId}`
        : urls.checkout.cart(params);

      navigate(cartUrl); // Then navigate using React Router
    }
  };

  const selectedProduct = products.find((p) => p.slug === selectedSlug);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-x-0 bottom-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-y-full"
                enterTo="translate-y-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-y-0"
                leaveTo="translate-y-full"
              >
                <Dialog.Panel className="pointer-events-auto w-full">
                  <div className="flex h-[80vh] flex-col rounded-t-3xl bg-white shadow-xl">
                    {/* Header */}
                    <div className="border-neutral-2-10 flex items-center justify-between border-b px-6 py-4">
                      <Dialog.Title
                        data-testid="custom-puzzle-sheet-title"
                        className="font-heading-condensed text-primary-2-80 text-xl leading-6 font-medium"
                      >
                        Create Your Custom Puzzle
                      </Dialog.Title>
                      <button
                        onClick={handleClose}
                        className="hover:bg-neutral-2-10 rounded-full p-2"
                        aria-label="Close"
                      >
                        <XMarkIcon className="text-neutral-2-80 h-6 w-6" />
                      </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6">
                      <div className="space-y-8">
                        {/* Step 1: Select Puzzle */}
                        <StepSection
                          number={1}
                          title="Select Puzzle Type"
                          completed={!!selectedSlug}
                        >
                          <SelectPuzzleType
                            products={products}
                            selectedSlug={selectedSlug}
                            onSelect={setSelectedSlug}
                            scrollToUpload={scrollToUpload}
                          />
                        </StepSection>

                        {/* Step 2: Upload Image */}
                        {selectedSlug && (
                          <div ref={uploadSectionRef}>
                            <StepSection
                              number={2}
                              title="Upload Your Image"
                              completed={uploadComplete}
                            >
                              <UploadImage
                                selectedProduct={selectedProduct!}
                                params={params}
                                originalUploadUrl={originalUploadUrl}
                                croppedUploadUrl={croppedUploadUrl}
                                onSuccess={(id) => {
                                  setCheckoutId(id);
                                  setUploadComplete(true);
                                }}
                                onProceedToCheckout={handleProceedToCheckout}
                              />
                            </StepSection>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
